import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { env } from '@/config/env';

/**
 * Pose l'adresse canonique de la page courante.
 *
 * Il y avait auparavant une balise figée dans index.html, pointant vers
 * « https://carflow.ci/ ». Comme index.html sert TOUTES les pages, chacune
 * déclarait que sa version de référence se trouvait à la racine d'un autre
 * domaine. Pour un moteur de recherche, c'est une instruction explicite de ne
 * pas indexer la page qu'il vient de lire — exactement l'inverse de ce qu'on
 * veut. Une canonique fausse désindexe ; une canonique absente ne fait rien.
 *
 * Ici, chaque page déclare sa propre adresse, reconstruite à partir de
 * VITE_SITE_URL. Les paramètres de requête sont volontairement écartés :
 * « /garages » et « /garages?ville=abidjan » montrent le même contenu filtré,
 * et les compter comme deux pages disperserait le référencement entre elles.
 *
 * Les écrans qui posent DÉJÀ leur propre canonique — la page ville, par
 * exemple — le font dans leur `useEffect`, qui s'exécute après celui-ci et a
 * donc le dernier mot.
 */
export function Canonical() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!env.siteUrl) return;

    const clean = pathname.replace(/\/+$/, '') || '/';
    const href = `${env.siteUrl.replace(/\/+$/, '')}${clean === '/' ? '/' : clean}`;

    let tag = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!tag) {
      tag = document.createElement('link');
      tag.rel = 'canonical';
      document.head.appendChild(tag);
    }
    tag.href = href;

    // `og:url` raconte la même chose aux réseaux sociaux : sans lui, un lien
    // partagé sur WhatsApp affiche l'aperçu de la page d'accueil.
    let og = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    if (!og) {
      og = document.createElement('meta');
      og.setAttribute('property', 'og:url');
      document.head.appendChild(og);
    }
    og.content = href;
  }, [pathname]);

  return null;
}
